import { useState } from 'react';

import { AlertTriangle, Plus, RefreshCw, Trash2, Webhook } from 'lucide-react';

import { FormInput, FormSelect } from '@/components/form';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useConfigurations } from '@/hooks/useConfigurations';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  useAssignInboundWhatsAppNumber,
  useCreateWhatsAppBusinessAccount,
  useDeleteWhatsAppBusinessAccount,
  useEnableWhatsAppCalling,
  useSubscribeWhatsAppBusinessAccount,
  useSyncWhatsAppNumbers,
  useUpdateWhatsAppBusinessAccount,
  useWhatsAppBusinessAccounts,
  useWhatsAppNumbers,
} from '@/hooks/useWhatsAppNumbers';
import { extractErrorMessage } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';

import { AddNumberDialog } from './whatsapp-numbers/AddNumberDialog';
import { CallingAction } from './whatsapp-numbers/CallingAction';

export const WhatsAppNumbersPage = () => {
  usePageTitle('WhatsApp Numbers');
  const isAdmin = useAuthStore((state) => state.user?.role === 'admin');
  const { toast } = useToast();
  const { data: numbers = [], isLoading } = useWhatsAppNumbers();
  const { data: configurations = [] } = useConfigurations();
  const { data: accounts = [] } = useWhatsAppBusinessAccounts();

  const syncMutation = useSyncWhatsAppNumbers();
  const assignMutation = useAssignInboundWhatsAppNumber();
  const createAccountMutation = useCreateWhatsAppBusinessAccount();
  const updateAccountMutation = useUpdateWhatsAppBusinessAccount();
  const deleteAccountMutation = useDeleteWhatsAppBusinessAccount();
  const subscribeAccountMutation = useSubscribeWhatsAppBusinessAccount();
  const enableCallingMutation = useEnableWhatsAppCalling();

  const [assignConfigByNumberId, setAssignConfigByNumberId] = useState<Record<string, string>>({});
  const [newAccount, setNewAccount] = useState({ waba_id: '', name: '', access_token: '' });
  const [tokenByAccountId, setTokenByAccountId] = useState<Record<string, string>>({});
  const [addNumberOpen, setAddNumberOpen] = useState(false);

  const handleAddAccount = async () => {
    if (!newAccount.waba_id.trim()) return;
    try {
      await createAccountMutation.mutateAsync({
        waba_id: newAccount.waba_id.trim(),
        name: newAccount.name.trim() || null,
        access_token: newAccount.access_token.trim() || null,
      });
      setNewAccount({ waba_id: '', name: '', access_token: '' });
      toast({
        variant: 'success',
        title: 'Business account added',
        description: 'Run Sync Numbers to import this WABA’s numbers.',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Could not add business account',
        description: extractErrorMessage(error),
      });
    }
  };

  const handleReplaceToken = async (id: string) => {
    try {
      await updateAccountMutation.mutateAsync({
        id,
        payload: { access_token: tokenByAccountId[id] ?? '' },
      });
      setTokenByAccountId((prev) => ({ ...prev, [id]: '' }));
      toast({
        variant: 'success',
        title: 'Token updated',
        description: (tokenByAccountId[id] ?? '').trim()
          ? 'This WABA now uses its own access token.'
          : 'Token cleared — this WABA falls back to the global access token.',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Could not update token',
        description: extractErrorMessage(error),
      });
    }
  };

  const handleToggleAccount = async (id: string, isActive: boolean) => {
    try {
      await updateAccountMutation.mutateAsync({ id, payload: { is_active: !isActive } });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Could not update business account',
        description: extractErrorMessage(error),
      });
    }
  };

  const handleSubscribeAccount = async (id: string) => {
    try {
      const result = await subscribeAccountMutation.mutateAsync(id);
      toast({
        variant: 'success',
        title: 'Webhooks subscribed',
        description: result.apps.length
          ? `Meta will now send this WABA's messages to: ${result.apps.join(', ')}.`
          : 'Subscribed — send a test message to confirm it reaches the webhook.',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Could not subscribe webhooks',
        description: extractErrorMessage(error),
      });
    }
  };

  const handleDeleteAccount = async (id: string) => {
    try {
      await deleteAccountMutation.mutateAsync(id);
      toast({ variant: 'success', title: 'Business account removed' });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Could not remove business account',
        description: extractErrorMessage(error),
      });
    }
  };

  const handleSync = async () => {
    try {
      const result = await syncMutation.mutateAsync();
      toast({
        variant: 'success',
        title: 'Numbers synced',
        description: `${result.count} numbers synced from WhatsApp Business.`,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Sync failed',
        description: extractErrorMessage(error),
      });
    }
  };

  const handleEnableCalling = async (numberId: string) => {
    try {
      const result = await enableCallingMutation.mutateAsync(numberId);
      toast({
        variant: result.enabled ? 'success' : 'destructive',
        title: result.enabled ? 'Calling enabled' : 'Calling not enabled',
        description: result.error ?? undefined,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Could not enable calling',
        description: extractErrorMessage(error),
      });
    }
  };

  const handleAssignInbound = async (numberId: string) => {
    try {
      const configurationId = assignConfigByNumberId[numberId] || null;
      await assignMutation.mutateAsync({
        numberId,
        payload: { configuration_id: configurationId },
      });
      toast({
        variant: 'success',
        title: 'Inbound assignment updated',
        description: configurationId
          ? 'Number is now mapped to the selected configuration.'
          : 'Inbound assignment cleared.',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Assignment failed',
        description: extractErrorMessage(error),
      });
    }
  };

  return (
    <div className="space-y-8">
      <header className="flex items-start justify-between">
        <div className="space-y-2">
          <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Telephony</p>
          <h1 className="text-3xl font-bold tracking-tight">WhatsApp Numbers</h1>
          <p className="text-muted-foreground max-w-3xl">
            Sync WhatsApp Business phone numbers from Meta and assign inbound routing to
            configurations. Numbers are managed via Meta Business Suite — sync to import them here.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={() => setAddNumberOpen(true)}>
              <Plus className="h-4 w-4" />
              Add number
            </Button>
          )}
          <Button onClick={handleSync} disabled={syncMutation.isPending}>
            <RefreshCw className={`h-4 w-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
            Sync Numbers
          </Button>
        </div>
      </header>

      {isAdmin && <AddNumberDialog open={addNumberOpen} onOpenChange={setAddNumberOpen} />}

      <Card>
        <CardHeader>
          <CardTitle>Business Accounts (WABAs)</CardTitle>
          <CardDescription>
            Sync numbers from more than one business manager. Leave a token empty to use the global
            WhatsApp access token. If you have no accounts here, the single WABA from settings is
            used — existing setups keep working untouched.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/20">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            <p className="text-muted-foreground text-xs">
              Every WABA listed here must be subscribed to the <strong>same Meta app</strong>.
              Inbound webhooks are verified with the single global app secret / verify token, so a
              WABA on a different app would fail verification. Only the access token may differ per
              WABA — set one here if your global token doesn&apos;t have permission on that WABA.
              After adding a WABA, hit <strong>Subscribe</strong> — Meta sends no messages for its
              numbers until the app is subscribed to that WABA&apos;s webhooks. Then{' '}
              <strong>Sync Numbers</strong> to import them.
            </p>
          </div>

          {accounts.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="text-muted-foreground text-left">
                    <th className="border-b px-3 py-2">WABA ID</th>
                    <th className="border-b px-3 py-2">Name</th>
                    <th className="border-b px-3 py-2">Token</th>
                    <th className="border-b px-3 py-2">Status</th>
                    <th className="border-b px-3 py-2">Replace token</th>
                    <th className="border-b px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((account) => (
                    <tr key={account.id}>
                      <td className="border-b px-3 py-2 font-mono">{account.waba_id}</td>
                      <td className="border-b px-3 py-2">{account.name || '-'}</td>
                      <td className="border-b px-3 py-2">
                        {account.has_token ? 'Own token' : 'Global token'}
                      </td>
                      <td className="border-b px-3 py-2">
                        {account.is_active ? 'Active' : 'Inactive'}
                      </td>
                      <td className="border-b px-3 py-2">
                        <div className="flex items-center gap-2">
                          <FormInput
                            type="password"
                            placeholder="New token (empty = global)"
                            value={tokenByAccountId[account.id] ?? ''}
                            onChange={(event) =>
                              setTokenByAccountId((prev) => ({
                                ...prev,
                                [account.id]: event.target.value,
                              }))
                            }
                            className="text-sm"
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReplaceToken(account.id)}
                            disabled={updateAccountMutation.isPending}
                          >
                            Save
                          </Button>
                        </div>
                      </td>
                      <td className="border-b px-3 py-2">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSubscribeAccount(account.id)}
                            disabled={subscribeAccountMutation.isPending}
                            title="Subscribe our Meta app to this WABA's webhooks — without this Meta sends us no messages for its numbers"
                          >
                            <Webhook className="h-4 w-4" />
                            Subscribe
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleToggleAccount(account.id, account.is_active)}
                            disabled={updateAccountMutation.isPending}
                          >
                            {account.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteAccount(account.id)}
                            disabled={deleteAccountMutation.isPending}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex flex-wrap items-end gap-2 rounded-md border p-3">
            <div className="flex-1 space-y-1" style={{ minWidth: 180 }}>
              <label className="text-xs font-medium">WABA ID</label>
              <FormInput
                placeholder="e.g. 1234567890"
                value={newAccount.waba_id}
                onChange={(event) =>
                  setNewAccount((prev) => ({ ...prev, waba_id: event.target.value }))
                }
                className="text-sm"
              />
            </div>
            <div className="flex-1 space-y-1" style={{ minWidth: 140 }}>
              <label className="text-xs font-medium">Name</label>
              <FormInput
                placeholder="e.g. Quality Part X"
                value={newAccount.name}
                onChange={(event) =>
                  setNewAccount((prev) => ({ ...prev, name: event.target.value }))
                }
                className="text-sm"
              />
            </div>
            <div className="flex-1 space-y-1" style={{ minWidth: 180 }}>
              <label className="text-xs font-medium">Access token (optional)</label>
              <FormInput
                type="password"
                placeholder="Empty = use global token"
                value={newAccount.access_token}
                onChange={(event) =>
                  setNewAccount((prev) => ({ ...prev, access_token: event.target.value }))
                }
                className="text-sm"
              />
            </div>
            <Button
              onClick={handleAddAccount}
              disabled={!newAccount.waba_id.trim() || createAccountMutation.isPending}
            >
              <Plus className="h-4 w-4" />
              Add
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Inventory</CardTitle>
          <CardDescription>
            WhatsApp Business numbers with configuration assignments for voice agent routing.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex min-h-[220px] items-center justify-center">
              <div className="border-primary h-10 w-10 animate-spin rounded-full border-b-2" />
            </div>
          ) : numbers.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No WhatsApp numbers yet. Click Sync to import from your Meta Business account.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="text-muted-foreground text-left">
                    <th className="border-b px-3 py-2">Name</th>
                    <th className="border-b px-3 py-2">Number</th>
                    <th className="border-b px-3 py-2">WABA</th>
                    <th className="border-b px-3 py-2">Status</th>
                    <th className="border-b px-3 py-2">Inbound Config</th>
                    <th className="border-b px-3 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {numbers.map((number) => (
                    <tr key={number.id}>
                      <td className="border-b px-3 py-2">{number.friendly_name || '-'}</td>
                      <td className="border-b px-3 py-2 font-mono">
                        {number.display_phone_number || number.phone_number}
                      </td>
                      <td className="border-b px-3 py-2 font-mono text-xs">
                        {accounts.find((a) => a.waba_id === number.waba_id)?.name ||
                          number.waba_id ||
                          '-'}
                      </td>
                      <td className="border-b px-3 py-2">
                        {number.is_active ? 'Active' : 'Inactive'}
                      </td>
                      <td className="border-b px-3 py-2">
                        <FormSelect
                          value={
                            assignConfigByNumberId[number.id] ??
                            number.inbound_configuration_id ??
                            ''
                          }
                          onChange={(event) =>
                            setAssignConfigByNumberId((prev) => ({
                              ...prev,
                              [number.id]: event.target.value,
                            }))
                          }
                        >
                          <option value="">Unassigned</option>
                          {configurations.map((config) => (
                            <option key={config.id} value={config.id}>
                              {config.name}
                            </option>
                          ))}
                        </FormSelect>
                      </td>
                      <td className="border-b px-3 py-2 text-right">
                        <div className="flex justify-end gap-2">
                          <CallingAction
                            capabilities={number.capabilities}
                            onEnable={() => handleEnableCalling(number.id)}
                            disabled={enableCallingMutation.isPending}
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleAssignInbound(number.id)}
                            disabled={assignMutation.isPending}
                          >
                            Save
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
